/**
 * PortfolioSection — S4 PR-b (Corrente de Compromissos, Item 404 faceta a):
 * a carteira de vendedor (`CustomerPortfolio`, Item 377 — o backend já existe;
 * esta PR dá a primeira superfície FE) ganha seção própria na ficha da Pessoa.
 *
 * Mostra o dono ATUAL da carteira (read gated `customer-portfolio-read` — a
 * seção só é montada pelo caller quando a cap está presente, então o fetch
 * nunca dispara sem ela) + permite atribuir/reatribuir/remover (gated
 * `customer-portfolio-manage`). `tenantId_personId` é `@@unique` no Engine:
 * no máximo 1 linha por pessoa (ativa ou não) — "reatribuir" reusa a mesma.
 */
import { useState } from 'react';

import { useCapability } from '../../hooks/use-capability';
import { useUsers } from '../../hooks/use-users';
import { useToastStore } from '../../stores/toast-store';

import { PersonSection } from './PersonSection';
import { useAssignPortfolio, usePersonPortfolio, useRemovePortfolio } from './use-persons';

import type { JSX } from 'react';

// Papéis operacionais elegíveis para dono de carteira — espelha
// `OPERATIONAL_ROLES` do Engine (`CustomerPortfolioService.assign`). O
// endpoint `/v1/users` só filtra 1 `role` por vez; buscamos `status=active`
// e filtramos os 3 papéis client-side (evita 3 requisições).
const OPERATIONAL_ROLES = new Set(['atendente', 'supervisor', 'gestor']);

export function PortfolioSection({ personId }: { personId: string }): JSX.Element {
  const canManage = useCapability('customer-portfolio-manage');
  // Revisão adversarial I2 — o dropdown depende de `users-read`; sem ela o
  // select ficaria vazio sem explicação (beco sem saída). Checamos a cap AQUI
  // para render um empty-state honesto no lugar do select.
  const canReadUsers = useCapability('users-read');
  const { data: entry, isLoading } = usePersonPortfolio(personId);
  const [selecting, setSelecting] = useState(false);
  const [operatorUserId, setOperatorUserId] = useState('');
  const assign = useAssignPortfolio(personId);
  const remove = useRemovePortfolio(personId);
  const toast = useToastStore((s) => s.push);

  // Gateado pela cap de gestão E pela de leitura de usuários — quem não pode
  // reatribuir/listar não paga a query (gatear o fetch via `enabled`).
  const { data: usersPage } = useUsers({ status: 'active', limit: 200 }, canManage && canReadUsers);
  const operators = canManage
    ? (usersPage?.data ?? []).filter((u) => OPERATIONAL_ROLES.has(u.primaryRole))
    : [];

  const startAssign = (): void => {
    setOperatorUserId(entry?.operatorUserId ?? '');
    setSelecting(true);
  };

  const save = (): void => {
    if (operatorUserId === '' || assign.isPending) return;
    assign.mutate(
      { operatorUserId },
      {
        onSuccess: () => {
          setSelecting(false);
          toast({ variant: 'success', title: 'Carteira atualizada', durationMs: 3000 });
        },
        onError: () => {
          toast({
            variant: 'error',
            title: 'Não foi possível atribuir a carteira.',
            durationMs: 6000,
          });
        },
      },
    );
  };

  const handleRemove = (): void => {
    if (entry === null || remove.isPending) return;
    remove.mutate(
      { id: entry.id, version: entry.version },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Cliente removido da carteira', durationMs: 3000 });
        },
        onError: () => {
          toast({
            variant: 'error',
            title: 'Não foi possível remover da carteira.',
            description: 'A carteira pode ter sido alterada — recarregue e tente novamente.',
            durationMs: 6000,
          });
        },
      },
    );
  };

  return (
    <PersonSection id="carteira" label="Carteira">
      {isLoading ? (
        <div className="person-empty">Carregando carteira…</div>
      ) : entry === null ? (
        <div className="person-empty">Este cliente não está em nenhuma carteira de operador.</div>
      ) : (
        <div className="person-bond-row">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            <span className="person-bond-row__title">Dono atual da carteira</span>
            {/* Nunca o ULID cru (revisão N1) — sem nome, rótulo honesto. */}
            <span className="person-bond-row__role">
              {entry.operatorName ?? 'Operador atribuído (sem nome)'}
            </span>
          </div>
          {canManage && (
            <button
              type="button"
              className="person-bond-row__manage"
              onClick={handleRemove}
              disabled={remove.isPending}
            >
              Remover da carteira
            </button>
          )}
        </div>
      )}

      {canManage && !selecting && (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          style={{ marginTop: 'var(--space-2)', alignSelf: 'flex-start' }}
          onClick={startAssign}
        >
          {entry === null ? 'Atribuir a um operador' : 'Reatribuir'}
        </button>
      )}

      {canManage && selecting && (
        <div
          className="flex flex-col gap-2"
          style={{
            marginTop: 'var(--space-2)',
            padding: 'var(--space-3)',
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            maxWidth: 420,
          }}
        >
          {canReadUsers ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
                Operador
              </span>
              <select
                value={operatorUserId}
                onChange={(e) => setOperatorUserId(e.target.value)}
                className="input"
                aria-label="Selecionar operador"
              >
                <option value="">Selecione…</option>
                {operators.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            // Revisão adversarial I2 — beco sem saída honesto: sem `users-read`
            // não há como listar operadores; dizemos o porquê em vez de um
            // select vazio mudo. Salvar permanece desabilitado (sem seleção).
            <div className="person-empty">
              Sem permissão para listar operadores — peça o acesso «users-read» ao administrador.
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={save}
              disabled={!canReadUsers || operatorUserId === '' || assign.isPending}
            >
              {assign.isPending ? 'Salvando…' : 'Salvar'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setSelecting(false)}
              disabled={assign.isPending}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </PersonSection>
  );
}
